import { artistSubjects, realArtworks } from './subjects';
import { aiModels } from './models';
import { shuffleArray } from '../utils/randomization';

// Subject categories and composition tags for content matching
export const subjectCategories = {
  stillLife: 'still life',
  portrait: 'portrait',
  landscape: 'landscape',
  architecture: 'architecture',
  multipleFigures: 'multiple figures',
  object: 'object',
  abstract: 'abstract'
};

export const compositionTags = {
  portrait: 'portrait orientation',
  landscape: 'landscape orientation',
  closeUp: 'close-up',
  midShot: 'mid-shot',
  wide: 'wide shot'
};

// Helper to create AI prompt with proper template
const createPrompt = (subject, artistName, artworkType = 'painting') => {
  return `a ${artworkType} of ${subject} in the style of ${artistName}`;
};

// Helper to randomly select items from array
const selectRandom = (array, count = 1) => {
  const shuffled = shuffleArray([...array]);
  return count === 1 ? shuffled[0] : shuffled.slice(0, count);
};

// Generate trials according to revised design (12 trials per artist)
export const generateTrials = (artist, models = aiModels) => {
  const trials = [];
  const subjects = artistSubjects[artist.id];
  const artworks = realArtworks[artist.id];
  
  if (!subjects || !artworks) {
    console.warn(`Missing data for artist: ${artist.id}`);
    return [];
  }

  // Ensure we have at least 2 models for cross-model comparisons
  const selectedModels = models.length >= 2 ? models : [...models, models[0]];
  
  // T1: Fidelity, content-matched (6 trials / 50%)
  // AI vs Real with matched subject categories
  for (let i = 0; i < 6; i++) {
    const model = selectedModels[i % selectedModels.length];
    const subjectType = i < 2 ? 'typical' : (i < 4 ? 'plausible' : 'typical');
    const aiSubject = subjects[subjectType][i % subjects[subjectType].length];
    const realArtwork = artworks[i % artworks.length];
    
    trials.push({
      trialFamily: 'T1',
      type: 'AI_vs_Real_matched',
      leftImage: `/images/ai/${model.id}/${artist.id}/${subjectType}/image${i+1}.jpg`,
      rightImage: `/images/real/${artist.id}/trial${i+1}.jpg`,
      leftType: 'ai',
      rightType: 'real',
      leftModel: model.id,
      leftPrompt: createPrompt(aiSubject, artist.name),
      rightTitle: realArtwork,
      subjectCategory: 'matched', // In real implementation, would be properly categorized
      composition: 'matched'
    });
  }
  
  // T2: Cross-model comparisons (3 trials / 25%)
  // AI vs AI from different models
  for (let i = 0; i < 3; i++) {
    const model1 = selectedModels[i % selectedModels.length];
    const model2 = selectedModels[(i + 1) % selectedModels.length];
    const subjectType = ['typical', 'plausible', 'anachronistic'][i];
    const subject = subjects[subjectType][i % subjects[subjectType].length];
    
    trials.push({
      trialFamily: 'T2',
      type: 'AI_vs_AI_crossmodel',
      leftImage: `/images/ai/${model1.id}/${artist.id}/${subjectType}/image${i+7}.jpg`,
      rightImage: `/images/ai/${model2.id}/${artist.id}/${subjectType}/image${i+7}.jpg`,
      leftType: 'ai',
      rightType: 'ai',
      leftModel: model1.id,
      rightModel: model2.id,
      leftPrompt: createPrompt(subject, artist.name),
      rightPrompt: createPrompt(subject, artist.name),
      subjectCategory: subjectType
    });
  }
  
  // T3: Human ceiling, real vs real (1 trial / 8%)
  // Two real artworks from same subject category
  const artwork1 = artworks[7] || artworks[0];
  const artwork2 = artworks[8] || artworks[1];
  
  trials.push({
    trialFamily: 'T3',
    type: 'Real_vs_Real',
    leftImage: `/images/real/${artist.id}/trial7.jpg`,
    rightImage: `/images/real/${artist.id}/trial8.jpg`,
    leftType: 'real',
    rightType: 'real',
    leftTitle: artwork1,
    rightTitle: artwork2,
    subjectCategory: 'matched'
  });
  
  // T4: Content-style disentanglement within model (2 trials / 17%)
  const disentanglementModel = selectedModels[0];
  
  // Trial 1: Typical vs Atypical-Plausible
  trials.push({
    trialFamily: 'T4',
    type: 'Typical_vs_Plausible',
    leftImage: `/images/ai/${disentanglementModel.id}/${artist.id}/typical/image10.jpg`,
    rightImage: `/images/ai/${disentanglementModel.id}/${artist.id}/plausible/image10.jpg`,
    leftType: 'ai-typical',
    rightType: 'ai-plausible',
    leftModel: disentanglementModel.id,
    rightModel: disentanglementModel.id,
    leftPrompt: createPrompt(subjects.typical[4], artist.name),
    rightPrompt: createPrompt(subjects.plausible[4], artist.name)
  });
  
  // Trial 2: Typical vs Atypical-Anachronistic
  trials.push({
    trialFamily: 'T4',
    type: 'Typical_vs_Anachronistic',
    leftImage: `/images/ai/${disentanglementModel.id}/${artist.id}/typical/image11.jpg`,
    rightImage: `/images/ai/${disentanglementModel.id}/${artist.id}/anachronistic/image11.jpg`,
    leftType: 'ai-typical',
    rightType: 'ai-anachronistic',
    leftModel: disentanglementModel.id,
    rightModel: disentanglementModel.id,
    leftPrompt: createPrompt(subjects.typical[5], artist.name),
    rightPrompt: createPrompt(subjects.anachronistic[5], artist.name)
  });
  
  return trials;
};

// Generate practice trials (2 trials as per revised design)
export const generatePracticeTrials = () => {
  const practiceArtist = {
    id: 'practice',
    name: 'Practice Artist'
  };
  
  return [
    {
      trialFamily: 'practice',
      type: 'practice_obvious',
      leftImage: '/images/practice/obvious_good.jpg',
      rightImage: '/images/practice/obvious_bad.jpg',
      leftType: 'real',
      rightType: 'ai',
      instruction: 'This is a practice trial. Select the image that better represents the artist\'s style.'
    },
    {
      trialFamily: 'practice', 
      type: 'practice_subtle',
      leftImage: '/images/practice/subtle1.jpg',
      rightImage: '/images/practice/subtle2.jpg',
      leftType: 'ai',
      rightType: 'real',
      instruction: 'This is another practice trial. Try to identify the more characteristic style.'
    }
  ];
};

// Generate catch trials (2 obvious trials for attention checking)
export const generateCatchTrials = (artist) => {
  return [
    {
      trialFamily: 'catch',
      type: 'catch_obvious',
      leftImage: `/images/real/${artist.id}/iconic.jpg`,
      rightImage: '/images/catch/completely_wrong_style.jpg',
      leftType: 'real',
      rightType: 'wrong',
      correctAnswer: 'left',
      artistName: artist.name
    },
    {
      trialFamily: 'catch',
      type: 'catch_obvious',
      leftImage: '/images/catch/completely_wrong_style2.jpg',
      rightImage: `/images/real/${artist.id}/iconic2.jpg`,
      leftType: 'wrong',
      rightType: 'real', 
      correctAnswer: 'right',
      artistName: artist.name
    }
  ];
};

// Select a trial for repetition (1 per artist block)
export const selectRepeatedTrial = (trials) => {
  // Select a T1 trial for repetition as it's the most important
  const t1Trials = trials.filter(t => t.trialFamily === 'T1');
  return t1Trials.length > 0 ? selectRandom(t1Trials) : trials[0];
};

// Apply randomization constraints (no more than 2 consecutive same model/subject type)
export const applyTrialConstraints = (trials) => {
  let constrained = [...trials];
  let swapsMade = true;
  let iterations = 0;
  const maxIterations = 100;
  
  while (swapsMade && iterations < maxIterations) {
    swapsMade = false;
    iterations++;
    
    for (let i = 0; i < constrained.length - 2; i++) {
      // Check for 3 consecutive trials with same model
      if (constrained[i].leftModel && 
          constrained[i].leftModel === constrained[i+1].leftModel &&
          constrained[i].leftModel === constrained[i+2].leftModel) {
        // Try to swap with a later trial
        for (let j = i + 3; j < constrained.length; j++) {
          if (constrained[j].leftModel !== constrained[i].leftModel) {
            [constrained[i+2], constrained[j]] = [constrained[j], constrained[i+2]];
            swapsMade = true;
            break;
          }
        }
      }
      
      // Check for 3 consecutive trials with same subject type
      const getSubjectType = (trial) => {
        if (trial.leftType?.includes('typical')) return 'typical';
        if (trial.leftType?.includes('plausible')) return 'plausible';
        if (trial.leftType?.includes('anachronistic')) return 'anachronistic';
        return trial.subjectCategory;
      };
      
      const type1 = getSubjectType(constrained[i]);
      const type2 = getSubjectType(constrained[i+1]);
      const type3 = getSubjectType(constrained[i+2]);
      
      if (type1 && type1 === type2 && type1 === type3) {
        // Try to swap with a later trial
        for (let j = i + 3; j < constrained.length; j++) {
          if (getSubjectType(constrained[j]) !== type1) {
            [constrained[i+2], constrained[j]] = [constrained[j], constrained[i+2]];
            swapsMade = true;
            break;
          }
        }
      }
    }
  }
  
  return constrained;
};

// Main function to generate a complete artist block
export const generateArtistBlock = (artist, models = aiModels, includePractice = false) => {
  let blockTrials = [];
  
  // Add practice trials if this is the first block
  if (includePractice) {
    blockTrials.push(...generatePracticeTrials());
  }
  
  // Generate main trials
  const mainTrials = generateTrials(artist, models);
  
  // Randomize main trials
  let randomizedTrials = shuffleArray(mainTrials);
  
  // Apply constraints to prevent streaks
  randomizedTrials = applyTrialConstraints(randomizedTrials);
  
  // Insert catch trials at random positions (but not first or last)
  const catchTrials = generateCatchTrials(artist);
  const catchPosition1 = Math.floor(Math.random() * (randomizedTrials.length - 2)) + 1;
  const catchPosition2 = Math.floor(Math.random() * (randomizedTrials.length - 2)) + 1;
  
  randomizedTrials.splice(catchPosition1, 0, catchTrials[0]);
  randomizedTrials.splice(catchPosition2 > catchPosition1 ? catchPosition2 + 1 : catchPosition2, 0, catchTrials[1]);
  
  // Select and mark a repeated trial
  const repeatedTrial = selectRepeatedTrial(mainTrials);
  const repeatPosition = Math.floor(Math.random() * (randomizedTrials.length - 4)) + 4;
  randomizedTrials.splice(repeatPosition, 0, {...repeatedTrial, isRepeated: true});
  
  blockTrials.push(...randomizedTrials);
  
  // Add metadata to each trial
  blockTrials = blockTrials.map((trial, index) => ({
    ...trial,
    trialNumber: index + 1,
    artistId: artist.id,
    artistName: artist.name
  }));
  
  return blockTrials;
};